/**
 * Where the edge thinks this visitor is.
 *
 * Served by the Pages worker at `/__geo`, not by the API: the visitors this
 * is for have no session, and `/api/auth/session` requires one. Same origin,
 * so it costs no extra connection and needs no CORS.
 *
 * Asked once per page load and shared, because it cannot change while the tab
 * is open, and because two components want it — the language suggestion and
 * the country field on sign-up.
 *
 * It never throws and never rejects. The whole feature is a nicety; a visitor
 * on a flaky connection, behind a blocker, or running the dev server (which
 * has no worker in front of it) gets `null` and the app behaves exactly as it
 * did before this existed.
 */
let pending: Promise<string | null> | null = null;

async function ask(): Promise<string | null> {
  try {
    const response = await fetch('/__geo', {
      headers: { accept: 'application/json' },
      // Carries no credentials and must never be read from a cache.
      credentials: 'omit',
      cache: 'no-store',
    });
    if (!response.ok) return null;
    const body: unknown = await response.json();
    const country = (body as { country?: unknown } | null)?.country;
    // Trust the shape no more than any other response body.
    return typeof country === 'string' && /^[A-Z]{2}$/.test(country) ? country : null;
  } catch {
    return null;
  }
}

/** The visitor's country, or null when it is not known. Never rejects. */
export function detectCountry(): Promise<string | null> {
  pending ??= ask();
  return pending;
}

/** Test seam: forgets the memoised answer. */
export function resetCountry(): void {
  pending = null;
}
